import { useState, useEffect, } from "react";

/* ─────────────────────────────────────────────
   GLOBAL STYLES  (injected once)
───────────────────────────────────────────── */
const globalCSS = `
  @import url('https://fonts.googleapis.com/css2?family=Sora:wght@300;400;500;600;700;800&family=DM+Sans:ital,wght@0,300;0,400;0,500;1,300&display=swap');

  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

  :root {
    /* Navy = primary brand → confianza, seriedad */
    --purple:       #1E3A5F;
    --purple-mid:   #25496F;
    --purple-light: #DBEAFE;
    --purple-pale:  #F0F7FF;
    /* Blue accent → acción, tecnología */
    --blue:         #2563EB;
    --blue-light:   #DBEAFE;
    /* Neutral */
    --dark:         #0F172A;
    --mid:          #334155;
    --gray:         #64748B;
    --border:       #E2E8F0;
    --bg:           #F8FAFC;
    --white:        #FFFFFF;
    --green:        #059669;
    --amber:        #D97706;
    /* Purple kept only as accent detail */
    --accent:       #6B21A8;
    --accent-light: #EDE9FE;
  }

  html { scroll-behavior: smooth; }

  body {
    font-family: 'DM Sans', sans-serif;
    background: var(--bg);
    color: var(--dark);
    line-height: 1.6;
    -webkit-font-smoothing: antialiased;
  }

  h1,h2,h3,h4,h5 {
    font-family: 'Sora', sans-serif;
    line-height: 1.2;
    color: var(--dark);
  }

  /* ── Animations ── */
  @keyframes fadeUp {
    from { opacity:0; transform:translateY(24px); }
    to   { opacity:1; transform:translateY(0); }
  }
  @keyframes float {
    0%,100% { transform: translateY(0px); }
    50%      { transform: translateY(-8px); }
  }
  @keyframes pulse-ring {
    0%   { transform:scale(0.95); box-shadow:0 0 0 0 rgba(37,99,235,0.4); }
    70%  { transform:scale(1);    box-shadow:0 0 0 12px rgba(107,33,168,0); }
    100% { transform:scale(0.95); box-shadow:0 0 0 0 rgba(107,33,168,0); }
  }
  @keyframes shimmer {
    0%   { background-position: -400px 0; }
    100% { background-position: 400px 0; }
  }
  @keyframes dash {
    to { stroke-dashoffset: 0; }
  }
  @keyframes spin {
    to { transform: rotate(360deg); }
  }

  .fade-up { animation: fadeUp 0.6s ease both; }
  .delay-1 { animation-delay: 0.1s; }
  .delay-2 { animation-delay: 0.2s; }
  .delay-3 { animation-delay: 0.3s; }
  .delay-4 { animation-delay: 0.4s; }
  .delay-5 { animation-delay: 0.5s; }

  /* ── Scroll reveal ── */
  .reveal { opacity:0; transform:translateY(28px); transition: opacity 0.55s ease, transform 0.55s ease; }
  .reveal.visible { opacity:1; transform:translateY(0); }

  /* ── Nav ── */
  .nav {
    position: fixed; top:0; left:0; right:0; z-index:100;
    padding: 0 5%;
    display: flex; align-items: center; justify-content: space-between;
    height: 68px;
    background: rgba(255,255,255,0.88);
    backdrop-filter: blur(16px);
    border-bottom: 1px solid var(--border);
    transition: box-shadow 0.3s;
  }
  .nav.scrolled { box-shadow: 0 4px 24px rgba(0,0,0,0.07); }
  .nav-brand { display:flex; align-items:center; gap:10px; text-decoration:none; }
  .nav-brand span { font-family:'Sora',sans-serif; font-weight:700; font-size:1.15rem; color:var(--dark); letter-spacing:-0.3px; }
  .nav-links { display:flex; align-items:center; gap:2rem; }
  .nav-links a { font-size:0.875rem; color:var(--gray); text-decoration:none; font-weight:500; transition:color 0.2s; }
  .nav-links a:hover { color:var(--blue); }
  .nav-cta {
    background: var(--purple); color:#fff !important; padding:9px 20px;
    border-radius:8px; font-size:0.875rem !important; font-weight:600 !important;
    transition: background 0.2s, transform 0.15s !important;
  }
  .nav-cta:hover { background: var(--purple-mid) !important; transform:translateY(-1px) !important; }

  /* ── Hero ── */
  .hero {
    min-height: 100vh;
    padding: 120px 5% 80px;
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 60px;
    align-items: center;
    background: linear-gradient(160deg, #F8FAFC 0%, #EFF6FF 60%, #F0F9FF 100%);
    position: relative;
    overflow: hidden;
  }
  .hero::before {
    content:'';
    position:absolute; top:-120px; right:-120px;
    width:500px; height:500px;
    background: radial-gradient(circle, rgba(37,99,235,0.07) 0%, transparent 70%);
    pointer-events:none;
  }
  .hero-badge {
    display:inline-flex; align-items:center; gap:8px;
    background: var(--blue-light); color:var(--blue);
    padding:6px 14px; border-radius:100px;
    font-size:0.8rem; font-weight:600; font-family:'Sora',sans-serif;
    margin-bottom:20px; letter-spacing:0.3px;
  }
  .hero-badge span { width:7px; height:7px; background:var(--blue); border-radius:50%; animation: pulse-ring 2s infinite; }
  .hero h1 {
    font-size: clamp(2.4rem, 4vw, 3.4rem);
    font-weight: 800;
    color: var(--dark);
    margin-bottom: 20px;
    letter-spacing: -1.5px;
    line-height: 1.1;
  }
  .hero h1 em {
    font-style:normal;
    background: linear-gradient(135deg, var(--blue) 0%, #0EA5E9 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }
  .hero p { font-size:1.05rem; color:var(--mid); max-width:480px; line-height:1.75; margin-bottom:36px; }
  .hero-btns { display:flex; gap:14px; flex-wrap:wrap; }
  .btn-primary {
    background: var(--purple); color:#fff;
    padding:14px 28px; border-radius:10px;
    font-family:'Sora',sans-serif; font-weight:600; font-size:0.9rem;
    border:none; cursor:pointer; text-decoration:none;
    display:inline-flex; align-items:center; gap:8px;
    transition: background 0.2s, transform 0.15s, box-shadow 0.2s;
    box-shadow: 0 4px 14px rgba(30,58,95,0.3);
  }
  .btn-primary:hover { background:var(--purple-mid); transform:translateY(-2px); box-shadow:0 8px 24px rgba(30,58,95,0.35); }
  .btn-secondary {
    background: transparent; color:var(--purple);
    padding:14px 28px; border-radius:10px;
    font-family:'Sora',sans-serif; font-weight:600; font-size:0.9rem;
    border:2px solid var(--purple); cursor:pointer; text-decoration:none;
    display:inline-flex; align-items:center; gap:8px;
    transition: background 0.2s, color 0.2s, transform 0.15s;
  }
  .btn-secondary:hover { background:var(--purple-light); transform:translateY(-2px); }

  /* ── Hero Visual ── */
  .hero-visual { animation: float 5s ease-in-out infinite; }
  .dashboard-mock {
    background: #fff;
    border-radius: 20px;
    padding: 24px;
    box-shadow: 0 24px 64px rgba(107,33,168,0.12), 0 4px 16px rgba(0,0,0,0.06);
    border: 1px solid rgba(107,33,168,0.1);
  }
  .mock-topbar { display:flex; align-items:center; gap:8px; margin-bottom:20px; }
  .mock-dot { width:10px; height:10px; border-radius:50%; }
  .mock-title { font-family:'Sora',sans-serif; font-size:0.8rem; font-weight:600; color:var(--gray); margin-left:auto; }
  .mock-kpis { display:grid; grid-template-columns:1fr 1fr 1fr; gap:10px; margin-bottom:18px; }
  .mock-kpi {
    background: #EFF6FF; border-radius:10px; padding:12px;
    border: 1px solid var(--blue-light);
  }
  .mock-kpi-label { font-size:0.65rem; color:var(--gray); font-weight:500; margin-bottom:4px; }
  .mock-kpi-val { font-family:'Sora',sans-serif; font-size:1.1rem; font-weight:700; color:var(--blue); }
  .mock-kpi-delta { font-size:0.65rem; color:var(--green); font-weight:600; }
  .mock-chart-area { background:#EFF6FF; border-radius:12px; padding:16px; margin-bottom:14px; }
  .mock-chart-label { font-size:0.7rem; color:var(--gray); margin-bottom:10px; font-weight:500; }
  .mock-bars { display:flex; align-items:flex-end; gap:6px; height:70px; }
  .mock-bar { flex:1; border-radius:4px 4px 0 0; transition:height 0.3s; }
  .mock-row { display:flex; align-items:center; gap:10px; padding:8px 0; border-top:1px solid var(--border); }
  .mock-row-label { font-size:0.72rem; color:var(--mid); flex:1; }
  .mock-row-badge { font-size:0.65rem; font-weight:600; padding:3px 8px; border-radius:100px; }
  .badge-ok  { background:#D1FAE5; color:#065F46; }
  .badge-pend{ background:#FEF3C7; color:#92400E; }

  /* ── Section commons ── */
  section { padding: 96px 5%; }
  .section-label { font-size:0.8rem; font-weight:700; color:var(--blue); letter-spacing:2px; text-transform:uppercase; margin-bottom:12px; font-family:'Sora',sans-serif; }
  .section-title { font-size:clamp(1.8rem,3vw,2.5rem); font-weight:800; letter-spacing:-0.8px; margin-bottom:16px; }
  .section-sub { font-size:1rem; color:var(--gray); max-width:560px; line-height:1.75; margin-bottom:56px; }

  /* ── Why us ── */
  .why-grid { display:grid; grid-template-columns:repeat(auto-fit, minmax(220px,1fr)); gap:20px; }
  .why-card {
    background:#fff; border-radius:16px; padding:28px 24px;
    border:1px solid var(--border);
    transition: transform 0.2s, box-shadow 0.2s, border-color 0.2s;
  }
  .why-card:hover { transform:translateY(-4px); box-shadow:0 16px 40px rgba(30,58,95,0.1); border-color:var(--blue-light); }
  .why-icon { font-size:1.8rem; margin-bottom:14px; }
  .why-card h3 { font-size:1rem; font-weight:700; margin-bottom:8px; color:var(--dark); }
  .why-card p { font-size:0.875rem; color:var(--gray); line-height:1.65; }

  /* ── Services ── */
  .services-bg { background: #0F2542; }
  .services-bg .section-title { color:#fff; }
  .services-bg .section-label { color: #93C5FD; }
  .services-bg .section-sub { color: #94A3B8; }
  .svc-grid { display:grid; grid-template-columns:repeat(auto-fit, minmax(280px,1fr)); gap:20px; }
  .svc-card {
    background: rgba(255,255,255,0.04);
    border:1px solid rgba(255,255,255,0.08);
    border-radius:16px; padding:28px 24px;
    transition: background 0.2s, border-color 0.2s, transform 0.2s;
    cursor:default;
  }
  .svc-card:hover { background:rgba(37,99,235,0.15); border-color:rgba(37,99,235,0.4); transform:translateY(-3px); }
  .svc-icon { font-size:1.6rem; margin-bottom:16px; }
  .svc-tag {
    display:inline-block; font-size:0.68rem; font-weight:700;
    padding:3px 10px; border-radius:100px; margin-bottom:14px;
    letter-spacing:0.5px; font-family:'Sora',sans-serif;
  }
  .tag-entrada { background:#1E3A5F; color:#93C5FD; }
  .tag-core    { background:#1E3A5F; color:#93C5FD; }
  .tag-recurrente { background:#064E3B; color:#6EE7B7; }
  .svc-card h3 { font-size:1.05rem; font-weight:700; color:#fff; margin-bottom:10px; }
  .svc-card p  { font-size:0.875rem; color:#94A3B8; line-height:1.7; margin-bottom:16px; }
  .svc-price { font-family:'Sora',sans-serif; font-size:1.2rem; font-weight:800; color:#93C5FD; }
  .svc-price-unit { font-size:0.75rem; color:#64748B; font-weight:400; margin-left:4px; }

  /* ── Pilot CTA strip ── */
  .pilot-strip {
    background: linear-gradient(135deg, #1E3A5F 0%, var(--blue) 100%);
    border-radius: 20px;
    padding: 48px 48px;
    display:flex; align-items:center; justify-content:space-between; gap:32px;
    flex-wrap:wrap;
    margin: 0 5% 0;
  }
  .pilot-strip h2 { font-size:1.75rem; font-weight:800; color:#fff; letter-spacing:-0.5px; margin-bottom:8px; }
  .pilot-strip p { color:rgba(255,255,255,0.8); font-size:0.95rem; max-width:500px; line-height:1.65; }
  .pilot-strip .btn-white {
    background:#fff; color:var(--blue);
    padding:14px 28px; border-radius:10px;
    font-family:'Sora',sans-serif; font-weight:700; font-size:0.9rem;
    border:none; cursor:pointer; text-decoration:none;
    white-space:nowrap;
    transition:transform 0.15s, box-shadow 0.2s;
    box-shadow:0 4px 16px rgba(0,0,0,0.2);
    display:inline-flex; align-items:center; gap:8px;
  }
  .pilot-strip .btn-white:hover { transform:translateY(-2px); box-shadow:0 8px 28px rgba(0,0,0,0.25); }

  /* ── Process ── */
  .process-steps { display:grid; grid-template-columns:repeat(auto-fit, minmax(160px,1fr)); gap:0; position:relative; }
  .process-steps::before {
    content:'';
    position:absolute; top:36px; left:5%; right:5%;
    height:2px; background:linear-gradient(90deg, var(--blue-light), #BAE6FD);
    z-index:0;
  }
  .process-step { text-align:center; padding:0 12px; position:relative; z-index:1; }
  .step-num {
    width:72px; height:72px; border-radius:50%;
    background:#fff; border:2px solid var(--purple-light);
    display:flex; align-items:center; justify-content:center;
    margin:0 auto 16px;
    font-family:'Sora',sans-serif; font-size:1.1rem; font-weight:800; color:var(--blue);
    box-shadow:0 4px 16px rgba(37,99,235,0.12);
    transition:background 0.2s, color 0.2s;
  }
  .process-step:hover .step-num { background:var(--blue); color:#fff; }
  .process-step h4 { font-size:0.9rem; font-weight:700; margin-bottom:6px; color:var(--dark); }
  .process-step p { font-size:0.78rem; color:var(--gray); line-height:1.6; }
  .step-time { display:inline-block; font-size:0.7rem; font-weight:600; color:var(--blue); background:var(--blue-light); padding:2px 8px; border-radius:100px; margin-top:8px; }

  /* ── Team ── */
  .team-card {
    background:#fff; border:1px solid var(--border); border-radius:20px;
    padding:32px 28px; max-width:360px; margin:0 auto;
    text-align:center;
    box-shadow:0 8px 32px rgba(107,33,168,0.08);
  }
  .team-avatar {
    width:88px; height:88px; border-radius:50%;
    background: linear-gradient(135deg, var(--blue-light), #BAE6FD);
    display:flex; align-items:center; justify-content:center;
    font-size:2.2rem; margin:0 auto 20px;
    border:3px solid var(--blue-light);
  }
  .team-card h3 { font-size:1.2rem; font-weight:800; margin-bottom:4px; }
  .team-card .role { color:var(--blue); font-weight:600; font-size:0.875rem; margin-bottom:16px; }
  .team-skills { display:flex; flex-wrap:wrap; gap:8px; justify-content:center; margin-bottom:16px; }
  .skill-chip { background:#EFF6FF; color:var(--blue); padding:4px 12px; border-radius:100px; font-size:0.72rem; font-weight:600; border:1px solid var(--blue-light); }
  .team-card p { font-size:0.875rem; color:var(--gray); line-height:1.7; }

  /* ── FAQ ── */
  .faq-list { max-width:720px; margin:0 auto; display:flex; flex-direction:column; gap:8px; }
  .faq-item { background:#fff; border:1px solid var(--border); border-radius:12px; overflow:hidden; transition:border-color 0.2s; }
  .faq-item.open { border-color:var(--blue-light); }
  .faq-q {
    width:100%; background:transparent; border:none; cursor:pointer;
    padding:18px 22px; display:flex; align-items:center; justify-content:space-between;
    font-family:'Sora',sans-serif; font-size:0.95rem; font-weight:600; color:var(--dark);
    text-align:left; transition:color 0.2s;
  }
  .faq-q:hover { color:var(--blue); }
  .faq-chevron { transition:transform 0.25s; flex-shrink:0; margin-left:16px; }
  .faq-item.open .faq-chevron { transform:rotate(180deg); }
  .faq-a { max-height:0; overflow:hidden; transition:max-height 0.3s ease, padding 0.2s; }
  .faq-a-inner { padding:0 22px 18px; font-size:0.9rem; color:var(--gray); line-height:1.75; }
  .faq-item.open .faq-a { max-height:300px; }

  /* ── Security section ── */
  .security-grid { display:grid; grid-template-columns:repeat(auto-fit, minmax(260px,1fr)); gap:16px; }
  .security-card {
    display:flex; gap:16px; align-items:flex-start;
    background:#fff; border:1px solid var(--border); border-radius:14px; padding:20px;
    transition:border-color 0.2s, box-shadow 0.2s;
  }
  .security-card:hover { border-color:var(--blue-light); box-shadow:0 8px 24px rgba(37,99,235,0.06); }
  .sec-icon { font-size:1.5rem; flex-shrink:0; margin-top:2px; }
  .security-card h4 { font-size:0.9rem; font-weight:700; margin-bottom:5px; }
  .security-card p  { font-size:0.82rem; color:var(--gray); line-height:1.6; }

  /* ── Contact ── */
  .contact-wrap {
    background: linear-gradient(135deg, #0F2542 0%, #1E3A5F 100%);
    border-radius:24px; padding:56px 48px;
    display:grid; grid-template-columns:1fr 1fr; gap:48px; align-items:start;
  }
  .contact-left h2 { font-size:2rem; font-weight:800; color:#fff; letter-spacing:-0.5px; margin-bottom:12px; }
  .contact-left p { color:rgba(255,255,255,0.75); font-size:0.95rem; line-height:1.75; margin-bottom:28px; }
  .contact-info-item { display:flex; align-items:center; gap:14px; margin-bottom:16px; }
  .contact-info-item .ci-icon { width:40px; height:40px; background:rgba(255,255,255,0.12); border-radius:10px; display:flex; align-items:center; justify-content:center; font-size:1.1rem; flex-shrink:0; }
  .contact-info-item .ci-label { font-size:0.75rem; color:rgba(255,255,255,0.55); font-weight:500; }
  .contact-info-item .ci-val { font-size:0.9rem; color:#fff; font-weight:600; }
  .contact-form { display:flex; flex-direction:column; gap:14px; }
  .form-row { display:grid; grid-template-columns:1fr 1fr; gap:14px; }
  .form-input, .form-select, .form-textarea {
    width:100%; padding:13px 16px; border-radius:10px;
    background:rgba(255,255,255,0.1); border:1px solid rgba(255,255,255,0.2);
    color:#fff; font-family:'DM Sans',sans-serif; font-size:0.9rem;
    outline:none; transition:background 0.2s, border-color 0.2s;
  }
  .form-input::placeholder, .form-textarea::placeholder { color:rgba(255,255,255,0.45); }
  .form-input:focus, .form-select:focus, .form-textarea:focus { background:rgba(255,255,255,0.15); border-color:rgba(255,255,255,0.5); }
  .form-select { cursor:pointer; }
  .form-select option { color:var(--dark); background:#fff; }
  .form-textarea { resize:vertical; min-height:100px; }
  .btn-submit {
    background:#fff; color:var(--purple);
    padding:14px 28px; border-radius:10px;
    font-family:'Sora',sans-serif; font-weight:700; font-size:0.9rem;
    border:none; cursor:pointer; width:100%;
    display:flex; align-items:center; justify-content:center; gap:8px;
    transition:transform 0.15s, box-shadow 0.2s;
    box-shadow:0 4px 14px rgba(0,0,0,0.15);
  }
  .btn-submit:hover { transform:translateY(-2px); box-shadow:0 8px 24px rgba(0,0,0,0.2); }

  /* ── Footer ── */
  .footer {
    background:var(--dark); color:#94A3B8;
    padding:40px 5% 28px;
    display:flex; align-items:center; justify-content:space-between; flex-wrap:wrap; gap:16px;
  }
  .footer-brand { display:flex; align-items:center; gap:10px; }
  .footer-brand span { font-family:'Sora',sans-serif; font-weight:700; color:#fff; font-size:1rem; }
  .footer-links { display:flex; gap:1.5rem; }
  .footer-links a { color:#64748B; text-decoration:none; font-size:0.85rem; transition:color 0.2s; }
  .footer-links a:hover { color:var(--purple-light); }
  .footer-copy { font-size:0.8rem; }

  /* ── Responsive ── */
  @media(max-width:900px) {
    .hero { grid-template-columns:1fr; text-align:center; }
    .hero p { margin:0 auto 36px; }
    .hero-btns { justify-content:center; }
    .hero-visual { order:-1; max-width:380px; margin:0 auto; }
    .process-steps::before { display:none; }
    .contact-wrap { grid-template-columns:1fr; }
    .form-row { grid-template-columns:1fr; }
    .pilot-strip { flex-direction:column; text-align:center; }
    .nav-links { display:none; }
  }
`;

/* ─────────────────────────────────────────────
   HOOK: scroll reveal
───────────────────────────────────────────── */
function useReveal() {
  useEffect(() => {
    const els = document.querySelectorAll(".reveal");
    const obs = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && e.target.classList.add("visible")),
      { threshold: 0.12 }
    );
    els.forEach((el) => obs.observe(el));
    return () => obs.disconnect();
  }, []);
}

/* ─────────────────────────────────────────────
   HOOK: nav scroll
───────────────────────────────────────────── */
function useNavScroll() {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", fn);
    return () => window.removeEventListener("scroll", fn);
  }, []);
  return scrolled;
}

/* ─────────────────────────────────────────────
   DATA
───────────────────────────────────────────── */
const WHY_CARDS = [
  { icon: "🎯", title: "Especialistas en finanzas + tecnología", desc: "Somos contadores auditores que también programan. Entendemos tu negocio desde adentro antes de proponer cualquier solución." },
  { icon: "⚡", title: "Piloto en 2 semanas", desc: "Sin contratos largos ni grandes inversiones iniciales. Arrancamos con un proceso específico para que veas resultados rápido." },
  { icon: "🔒", title: "100% confidencial", desc: "Firmamos NDA antes de cualquier intercambio de información. Tus datos no salen de tu empresa." },
  { icon: "🛠️", title: "Soluciones que se usan", desc: "No entregamos reportes que quedan en un cajón. Automatizamos procesos reales que tu equipo adopta desde el primer día." },
];

const SERVICES = [
  {
    icon: "🔍", tag: "tag-entrada", tagLabel: "ENTRADA",
    title: "Diagnóstico de Procesos",
    desc: "Sesión de 90 min donde mapeamos tus procesos, identificamos tareas repetitivas y entregamos una hoja de ruta concreta.",
  },
  {
    icon: "⚙️", tag: "tag-core", tagLabel: "CORE",
    title: "Automatización con IA",
    desc: "Desarrollamos soluciones a medida: conciliaciones automáticas, dashboards BI, integración OCR de facturas, y más.",
  },
  {
    icon: "📊", tag: "tag-core", tagLabel: "CORE",
    title: "Implementación ERP",
    desc: "Configuración e integración de Odoo, Softland o Nubox según tu operación. Sin configuraciones genéricas.",
  },
  {
    icon: "📈", tag: "tag-core", tagLabel: "CORE",
    title: "Dashboard Financiero",
    desc: "Conectamos tu software contable con reportería BI + análisis predictivo para tomar decisiones con datos reales.",
  },
  {
    icon: "💼", tag: "tag-entrada", tagLabel: "ASESORÍA",
    title: "Consultoría Contable y Tributaria",
    desc: "F29, F22, conciliaciones, análisis de balance. Apoyo contable integral para empresas que necesitan orden.",
  },
  {
    icon: "🔄", tag: "tag-recurrente", tagLabel: "RECURRENTE",
    title: "Soporte Mensual",
    desc: "Mantenimiento de automatizaciones, soporte prioritario y horas de desarrollo incluidas. Escala cuando tu empresa crece.",
  },
];

const PROCESS_STEPS = [
  { num: "01", title: "Diagnóstico", desc: "Reunión inicial para entender tus procesos", time: "1-2 días" },
  { num: "02", title: "Propuesta", desc: "Alcance y precio fijo por escrito", time: "1 día" },
  { num: "03", title: "NDA + Contrato", desc: "Confidencialidad garantizada antes de empezar", time: "1 día" },
  { num: "04", title: "Desarrollo", desc: "Construcción con revisiones intermedias", time: "1-5 sem." },
  { num: "05", title: "Entrega", desc: "Capacitación y documentación completa", time: "1 día" },
  { num: "06", title: "Soporte", desc: "Acompañamiento según plan elegido", time: "Continuo" },
];

const SECURITY = [
  { icon: "📄", title: "NDA antes de empezar", desc: "Firmamos acuerdo de confidencialidad antes de cualquier intercambio de información sensible." },
  { icon: "🔑", title: "Acceso mínimo necesario", desc: "Trabajamos con accesos temporales de solo lectura cuando es posible. No almacenamos credenciales." },
  { icon: "🛡️", title: "Contrato de alcance fijo", desc: "Cada proyecto incluye contrato detallado: alcance, entregables, plazos y propiedad intelectual." },
  { icon: "💾", title: "Transferencia cifrada", desc: "Toda información sensible se mueve por canales cifrados. No usamos correo sin encriptar para datos financieros." },
  { icon: "🗑️", title: "Eliminación post-proyecto", desc: "Al finalizar, tus datos son eliminados o devueltos según tus instrucciones. Lo certificamos por escrito." },
  { icon: "📋", title: "Cumplimiento Ley 19.628", desc: "Operamos conforme a la Ley de Protección de Datos Personales de Chile y mejores prácticas de ciberseguridad." },
];

const FAQS = [
  { q: "¿Qué servicios ofrecen exactamente?", a: "Automatización de procesos contables con IA y VBA, dashboards financieros con BI, implementación de ERP (Odoo, Softland, Nubox), asesoría contable y tributaria, y planes de soporte mensual. Todo personalizado según la realidad de tu empresa." },
  { q: "¿Trabajan con empresas de todos los tamaños?", a: "Sí. Nuestro enfoque está pensado para PYMEs y startups que quieren modernizarse sin el costo de un equipo tecnológico interno. También trabajamos con empresas medianas que necesitan automatizar procesos específicos." },
  { q: "¿Cómo funciona el diagnóstico gratuito?", a: "Es una sesión de 45 minutos donde mapeamos tu proceso más crítico, estimamos el ahorro real en horas y costos, y te entregamos una propuesta con alcance y precio fijo. No hay desarrollo ni entregables técnicos en esta etapa — solo análisis experto y claridad antes de comprometerte con cualquier inversión." },
  { q: "¿Trabajan de forma presencial o remota?", a: "Principalmente remoto, lo que nos permite trabajar con empresas de todo Chile. Para proyectos más complejos o diagnósticos iniciales podemos acordar reuniones presenciales en Santiago." },
  { q: "¿Cómo calculan el costo?", a: "Cotizamos por valor entregado, no por hora. Cada propuesta incluye un precio fijo con alcance definido. Antes de cotizar hacemos un diagnóstico para entender bien qué se necesita y no cobrar de más ni de menos." },
  { q: "¿Quién trabaja en mis proyectos?", a: "Dylan Monje, fundador de Updai, es quien lleva los proyectos directamente. Contador Auditor titulado con 4+ años de experiencia en empresas como Coopeuch, Rankmi y Grupo Leben, y formación en desarrollo fullstack." },
];

/* ─────────────────────────────────────────────
   SUB-COMPONENTS
───────────────────────────────────────────── */
function Logo({ size = 36 }) {
  return (
    <img
      src="/logo_option_A_bold_arrow.png"
      alt="Updai logo"
      width={size}
      height={size}
      style={{ objectFit: "contain", display: "block" }}
    />
  );
}

function DashboardMock() {
  const barHeights = [45, 60, 35, 75, 55, 80, 65];
  const colors = ["#BFDBFE","#93C5FD","#60A5FA","#3B82F6","#2563EB","#1D4ED8","#1E3A5F"];
  return (
    <div className="dashboard-mock">
      <div className="mock-topbar">
        <div className="mock-dot" style={{background:"#FF5F57"}} />
        <div className="mock-dot" style={{background:"#FFBD2E"}} />
        <div className="mock-dot" style={{background:"#28CA41"}} />
        <span className="mock-title">Panel Financiero</span>
      </div>
      <div className="mock-kpis">
        {[
          { label:"Ingresos mes", val:"$4.2M", delta:"+12%" },
          { label:"Conciliado", val:"98%", delta:"✓ ok" },
          { label:"Horas ahorradas", val:"40h", delta:"este mes" },
        ].map((k, i) => (
          <div className="mock-kpi" key={i}>
            <div className="mock-kpi-label">{k.label}</div>
            <div className="mock-kpi-val">{k.val}</div>
            <div className="mock-kpi-delta">{k.delta}</div>
          </div>
        ))}
      </div>
      <div className="mock-chart-area">
        <div className="mock-chart-label">Cierre contable mensual</div>
        <div className="mock-bars">
          {barHeights.map((h, i) => (
            <div key={i} className="mock-bar" style={{ height: `${h}%`, background: colors[i] }} />
          ))}
        </div>
      </div>
      {[
        { label:"Conciliación bancaria", badge:"badge-ok", badgeText:"Automatizado" },
        { label:"F29 declarado", badge:"badge-ok", badgeText:"Listo" },
        { label:"Análisis balance", badge:"badge-pend", badgeText:"En proceso" },
      ].map((r, i) => (
        <div className="mock-row" key={i}>
          <span className="mock-row-label">{r.label}</span>
          <span className={`mock-row-badge ${r.badge}`}>{r.badgeText}</span>
        </div>
      ))}
    </div>
  );
}

function FaqItem({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div className={`faq-item ${open ? "open" : ""}`}>
      <button className="faq-q" onClick={() => setOpen(!open)}>
        {q}
        <svg className="faq-chevron" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>
      <div className="faq-a">
        <div className="faq-a-inner">{a}</div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────
   MAIN APP
───────────────────────────────────────────── */
export default function App() {
  useReveal();
  const scrolled = useNavScroll();
  const [formData, setFormData] = useState({ name:"", email:"", phone:"", service:"", message:"" });
  const [formState, setFormState] = useState("idle"); // idle | sending | success | error

  // ─── FORMSPREE ───────────────────────────────────────────────────────────
  // 1. Ve a https://formspree.io y crea una cuenta gratuita
  // 2. Crea un nuevo formulario y copia tu endpoint (ej: https://formspree.io/f/xyzabcde)
  // 3. Reemplaza la URL de abajo con tu endpoint real
  const FORMSPREE_ENDPOINT = "https://formspree.io/f/xlgwkkqj";

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormState("sending");
    try {
      const res = await fetch(FORMSPREE_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json", "Accept": "application/json" },
        body: JSON.stringify({
          nombre:   formData.name,
          email:    formData.email,
          telefono: formData.phone,
          servicio: formData.service,
          mensaje:  formData.message,
        }),
      });
      if (res.ok) {
        setFormState("success");
        setFormData({ name:"", email:"", phone:"", service:"", message:"" });
        setTimeout(() => setFormState("idle"), 6000);
      } else {
        setFormState("error");
        setTimeout(() => setFormState("idle"), 5000);
      }
    } catch {
      setFormState("error");
      setTimeout(() => setFormState("idle"), 5000);
    }
  };

  return (
    <>
      <style>{globalCSS}</style>

      {/* ── NAV ── */}
      <nav className={`nav ${scrolled ? "scrolled" : ""}`}>
        <a href="#inicio" className="nav-brand">
          <Logo />
          <span>UPDAI</span>
        </a>
        <div className="nav-links">
          <a href="#por-que">¿Por qué nosotros?</a>
          <a href="#servicios">Servicios</a>
          <a href="#proceso">Proceso</a>
          <a href="#equipo">Equipo</a>
          <a href="#faq">FAQ</a>
          <a href="#contacto" className="nav-cta">Consulta gratuita</a>
        </div>
      </nav>

      {/* ── HERO ── */}
      <section id="inicio" className="hero">
        <div>
          <div className="hero-badge fade-up">
            <span />
            Automatización financiera con IA
          </div>
          <h1 className="fade-up delay-1">
            Procesos contables<br />
            que <em>trabajan solos</em>
          </h1>
          <p className="fade-up delay-2">
            Automatizamos tus conciliaciones, reportes y flujos contables con inteligencia artificial para que tu equipo se enfoque en lo que realmente importa.
          </p>
          <div className="hero-btns fade-up delay-3">
            <a href="#contacto" className="btn-primary">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12 19.79 19.79 0 0 1 1.63 3.45 2 2 0 0 1 3.6 1.27h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 8.91a16 16 0 0 0 6.29 6.29l.73-.73a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/></svg>
              Agendar consulta gratuita
            </a>
            <a href="#servicios" className="btn-secondary">
              Ver servicios →
            </a>
          </div>
        </div>
        <div className="hero-visual fade-up delay-4">
          <DashboardMock />
        </div>
      </section>

      {/* ── WHY US ── */}
      <section id="por-que" style={{background:"#fff"}}>
        <div className="reveal">
          <p className="section-label">¿Por qué nosotros?</p>
          <h2 className="section-title">Lo que nos hace diferentes</h2>
          <p className="section-sub">No somos una agencia genérica. Combinamos formación contable con ingeniería de software para entregar soluciones que tu equipo realmente adopta.</p>
        </div>
        <div className="why-grid reveal" style={{animationDelay:"0.1s"}}>
          {WHY_CARDS.map((c, i) => (
            <div className="why-card" key={i}>
              <div className="why-icon">{c.icon}</div>
              <h3>{c.title}</h3>
              <p>{c.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── SERVICES ── */}
      <section id="servicios" className="services-bg">
        <div className="reveal">
          <p className="section-label" style={{color:"#93C5FD"}}>Servicios</p>
          <h2 className="section-title" style={{color:"#fff"}}>Soluciones para cada etapa</h2>
          <p className="section-sub" style={{color:"#94A3B8"}}>Desde el primer diagnóstico hasta el soporte mensual. Cada servicio tiene precio transparente y alcance definido.</p>
        </div>
        <div className="svc-grid reveal">
          {SERVICES.map((s, i) => (
            <div className="svc-card" key={i}>
              <div className="svc-icon">{s.icon}</div>
              <span className={`svc-tag ${s.tag}`}>{s.tagLabel}</span>
              <h3>{s.title}</h3>
              <p>{s.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── DIAGNÓSTICO STRIP ── */}
      <section style={{background:"#F0F7FF", padding:"80px 5%"}}>
        <div className="reveal" style={{textAlign:"center", marginBottom:"40px"}}>
          <p className="section-label">Diagnóstico express</p>
          <h2 className="section-title">Antes de invertir, conoce tu ROI</h2>
          <p className="section-sub" style={{margin:"0 auto"}}>Una sesión de 45 minutos para mapear tu proceso, estimar cuántas horas puedes ahorrar y recibir una propuesta concreta. Sin costo, sin compromiso.</p>
        </div>
        <div className="pilot-strip reveal">
          <div>
            <h2>45 minutos, propuesta en mano</h2>
            <p>Analizamos tu proceso más crítico, calculamos el ahorro real en horas y costos, y te entregamos una propuesta con alcance y precio fijo. Tú decides si avanzamos — con toda la información sobre la mesa.</p>
          </div>
          <a href="#contacto" className="btn-white">
            Agendar diagnóstico gratuito →
          </a>
        </div>
      </section>

      {/* ── PROCESS ── */}
      <section id="proceso" style={{background:"#fff"}}>
        <div className="reveal" style={{textAlign:"center"}}>
          <p className="section-label">Cómo trabajamos</p>
          <h2 className="section-title">6 pasos, sin sorpresas</h2>
          <p className="section-sub" style={{margin:"0 auto 56px"}}>Un proceso claro desde el primer contacto hasta el soporte post-entrega.</p>
        </div>
        <div className="process-steps reveal">
          {PROCESS_STEPS.map((s, i) => (
            <div className="process-step" key={i}>
              <div className="step-num">{s.num}</div>
              <h4>{s.title}</h4>
              <p>{s.desc}</p>
              <span className="step-time">⏱ {s.time}</span>
            </div>
          ))}
        </div>
      </section>

      {/* ── SECURITY ── */}
      <section style={{background:""}} id="seguridad">
        <div className="reveal">
          <p className="section-label">Seguridad</p>
          <h2 className="section-title">Tu información, protegida en cada paso</h2>
          <p className="section-sub">Manejar datos financieros requiere máxima confianza. Aquí nuestro marco de seguridad.</p>
        </div>
        <div className="security-grid reveal">
          {SECURITY.map((s, i) => (
            <div className="security-card" key={i}>
              <span className="sec-icon">{s.icon}</span>
              <div>
                <h4>{s.title}</h4>
                <p>{s.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ── TEAM ── */}
      <section id="equipo" style={{background:"#fff"}}>
        <div className="reveal" style={{textAlign:"center"}}>
          <p className="section-label">El equipo</p>
          <h2 className="section-title">Quién trabaja en tu proyecto</h2>
          <p className="section-sub" style={{margin:"0 auto 48px"}}>Modelo boutique: un interlocutor directo, sin burocracia, con los especialistas justos para cada proyecto.</p>
        </div>
        <div className="reveal" style={{display:"grid", gridTemplateColumns:"repeat(auto-fit, minmax(260px,1fr))", gap:"20px", maxWidth:"900px", margin:"0 auto"}}>
          <div className="team-card" style={{maxWidth:"100%"}}>
            <div className="team-avatar">👨‍💻</div>
            <h3>Dylan Monje Fuentes</h3>
            <p className="role">Fundador & Consultor Principal</p>
            <p>Contador Auditor y desarrollador fullstack. Soy tu punto de contacto desde el diagnóstico hasta la entrega. Cada proyecto pasa por mis manos.</p>
          </div>
          <div className="team-card" style={{maxWidth:"100%"}}>
            <div className="team-avatar" style={{fontSize:"1.6rem"}}>🤝</div>
            <h3>Red de Colaboradores</h3>
            <p className="role">Especialistas por proyecto</p>
            <p>Según el alcance, sumamos desarrolladores, diseñadores UX o contadores especializados de nuestra red. El equipo se arma a medida de lo que necesitas.</p>
          </div>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section id="faq">
        <div className="reveal" style={{textAlign:"center"}}>
          <p className="section-label">Preguntas frecuentes</p>
          <h2 className="section-title">Todo lo que necesitas saber</h2>
          <p className="section-sub" style={{margin:"0 auto 48px"}}>Si no encuentras lo que buscas, escríbenos directamente.</p>
        </div>
        <div className="faq-list reveal">
          {FAQS.map((f, i) => <FaqItem key={i} {...f} />)}
        </div>
      </section>

      {/* ── CONTACT ── */}
      <section id="contacto" style={{paddingTop:0}}>
        <div className="contact-wrap reveal">
          <div className="contact-left">
            <h2>¿Listo para automatizar tu empresa?</h2>
            <p>Agenda una consulta gratuita de 30 minutos. Sin compromiso, sin costo. Te mostramos cómo la IA puede resolver tu mayor dolor administrativo.</p>
            {[
              { icon:"📱", label:"WhatsApp", val:"+56 9 XXXX XXXX" },
              { icon:"✉️", label:"Email", val:"contacto@updai.app" },
              { icon:"📍", label:"Ubicación", val:"Santiago, Chile" },
            ].map((ci, i) => (
              <div className="contact-info-item" key={i}>
                <div className="ci-icon">{ci.icon}</div>
                <div>
                  <div className="ci-label">{ci.label}</div>
                  <div className="ci-val">{ci.val}</div>
                </div>
              </div>
            ))}
          </div>

          <form className="contact-form" onSubmit={handleSubmit}>
            <div className="form-row">
              <input
                className="form-input" placeholder="Nombre completo *" required
                value={formData.name} onChange={e => setFormData({...formData, name:e.target.value})}
                disabled={formState === "sending"}
              />
              <input
                className="form-input" type="email" placeholder="Email *" required
                value={formData.email} onChange={e => setFormData({...formData, email:e.target.value})}
                disabled={formState === "sending"}
              />
            </div>
            <input
              className="form-input" placeholder="Teléfono / WhatsApp"
              value={formData.phone} onChange={e => setFormData({...formData, phone:e.target.value})}
              disabled={formState === "sending"}
            />
            <select
              className="form-select" required
              value={formData.service} onChange={e => setFormData({...formData, service:e.target.value})}
              disabled={formState === "sending"}
            >
              <option value="">¿Qué servicio te interesa? *</option>
              <option>Diagnóstico de Procesos</option>
              <option>Automatización con IA</option>
              <option>Implementación ERP</option>
              <option>Dashboard Financiero</option>
              <option>Consultoría Contable / Tributaria</option>
              <option>Soporte Mensual</option>
              <option>Diagnóstico express gratuito</option>
            </select>
            <textarea
              className="form-textarea"
              placeholder="Cuéntanos tu principal problema o proceso a automatizar *"
              required
              value={formData.message} onChange={e => setFormData({...formData, message:e.target.value})}
              disabled={formState === "sending"}
            />

            {/* Estado de error */}
            {formState === "error" && (
              <div style={{
                background:"rgba(239,68,68,0.15)", border:"1px solid rgba(239,68,68,0.4)",
                borderRadius:"8px", padding:"10px 14px",
                fontSize:"0.875rem", color:"#FCA5A5", lineHeight:"1.5"
              }}>
                ⚠️ Hubo un error al enviar. Escríbenos directamente a <strong>contacto@updai.app</strong>
              </div>
            )}

            <button
              type="submit"
              className="btn-submit"
              disabled={formState === "sending" || formState === "success"}
            >
              {formState === "sending" && (
                <><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{animation:"spin 1s linear infinite"}}><path d="M21 12a9 9 0 1 1-6.219-8.56"/></svg> Enviando...</>
              )}
              {formState === "success" && "✅ ¡Mensaje enviado! Te contactamos pronto"}
              {(formState === "idle" || formState === "error") && (
                <><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg> Enviar consulta</>
              )}
            </button>

            <p style={{fontSize:"0.75rem", color:"rgba(255,255,255,0.4)", textAlign:"center", marginTop:"-4px"}}>
              🔒 Tu información es confidencial. Respondemos en máximo 24 horas hábiles.
            </p>
          </form>
        </div>
      </section>

      {/* ── FOOTER ── */}
      <footer className="footer">
        <div className="footer-brand">
          <Logo size={28} />
          <span>UPDAI</span>
        </div>
        <p className="footer-copy">© 2025 Updai. Todos los derechos reservados.</p>
        <div className="footer-links">
          <a href="#por-que">¿Por qué nosotros?</a>
          <a href="#servicios">Servicios</a>
          <a href="#contacto">Contacto</a>
        </div>
      </footer>
    </>
  );
}